/**
 * AiSuggestionPreview — calm editorial preview box for AI suggestions.
 * Renders below the textarea; editor must explicitly accept or dismiss.
 * Optionally shows current text for comparison and a lightweight change summary.
 */
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { useMemo } from "react";

interface AiSuggestionPreviewProps {
  suggestion: string;
  currentText?: string;
  onAccept: () => void;
  onDismiss: () => void;
}

// ─── Lightweight change summary (no diff engine) ─────────

function buildChangeSummary(current: string, suggested: string): string[] {
  const bullets: string[] = [];
  const curLen = current.trim().length;
  const sugLen = suggested.trim().length;

  // Length change
  const ratio = sugLen / Math.max(curLen, 1);
  if (ratio < 0.7) {
    bullets.push("Texten har kortats ned");
  } else if (ratio > 1.4) {
    bullets.push("Texten har utvecklats");
  }

  // Tone / structure hints based on simple heuristics
  const curSentences = current.split(/[.!?]+/).filter((s) => s.trim().length > 0).length;
  const sugSentences = suggested.split(/[.!?]+/).filter((s) => s.trim().length > 0).length;
  if (sugSentences < curSentences && curSentences > 1) {
    bullets.push("Färre meningar, mer koncentrerat");
  } else if (sugSentences > curSentences + 1) {
    bullets.push("Fler meningar för mer detalj");
  }

  // Word overlap — rough measure of how much changed
  const curWords = new Set(current.toLowerCase().split(/\s+/).filter(Boolean));
  const sugWords = suggested.toLowerCase().split(/\s+/).filter(Boolean);
  const kept = sugWords.filter((w) => curWords.has(w)).length;
  const overlap = kept / Math.max(sugWords.length, 1);
  if (overlap < 0.35) {
    bullets.push("Omformulerad med nytt perspektiv");
  } else if (overlap < 0.65) {
    bullets.push("Tonen och formuleringarna har justerats");
  }

  // Cap at 3 bullets
  return bullets.slice(0, 3);
}

// ─── Component ──────────────────────────────────

export function AiSuggestionPreview({ suggestion, currentText, onAccept, onDismiss }: AiSuggestionPreviewProps) {
  const showCurrent = currentText && currentText.trim().length > 0;

  const changeSummary = useMemo(() => {
    if (!showCurrent) return [];
    return buildChangeSummary(currentText!, suggestion);
  }, [showCurrent, currentText, suggestion]);

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2.5 animate-in fade-in-0 slide-in-from-top-1 duration-200">
      {showCurrent && (
        <div className="space-y-1">
          <p className="text-[11px] font-medium text-muted-foreground/70 tracking-wide uppercase">
            Nuvarande text
          </p>
          <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed line-clamp-3">
            {currentText}
          </p>
        </div>
      )}

      <div className="space-y-1">
        <p className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase">
          AI-förslag
        </p>
        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
          {suggestion}
        </p>
      </div>

      {/* Lightweight change summary */}
      {showCurrent && changeSummary.length > 0 && (
        <div className="space-y-1 pt-0.5">
          <p className="text-[11px] font-medium text-muted-foreground/70 tracking-wide uppercase">
            Vad som ändrats
          </p>
          <ul className="space-y-0.5">
            {changeSummary.map((bullet, i) => (
              <li key={i} className="text-[11px] text-muted-foreground leading-snug flex items-start gap-1.5">
                <span className="text-muted-foreground/50 mt-px">·</span>
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 pt-0.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-3 text-xs gap-1.5"
          onClick={onAccept}
        >
          <Check className="h-3 w-3" />
          Använd förslaget
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-3 text-xs text-muted-foreground gap-1.5"
          onClick={onDismiss}
        >
          <X className="h-3 w-3" />
          Behåll min text
        </Button>
      </div>
    </div>
  );
}
